/**
 * EntityTable — config-driven, server-sorted, page-number-paginated table.
 *
 * Features:
 * - Config-driven columns (sortable flag, custom cell renderer, helpKey)
 * - Server-side sort: clicking a sortable header calls onSortChange()
 * - Server-side filters: rendered from config.filters; calls onFilterChange()
 * - Debounced search (300ms) → onSearchChange()
 * - Numbered pagination on desktop; prev/next arrows on ≤768px
 * - Accurate total from backend Firestore aggregation count
 * - Bulk-select checkbox column (selection state managed here)
 * - Row hover action menu: view / edit / archive-activate (per config)
 * - Responsive: table on desktop, card list on ≤768px
 * - Empty / loading / error states
 *
 * Tags: candeza-{testIdPrefix}-table, -row-{id}, -filter-{key},
 *       -sort-{col}, -pagination, -count, -search
 */

import React, { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button } from '@/components/common/Button';
import { Dropdown } from '@/components/common/Dropdown';
import { SearchBar } from '@/components/common/SearchBar';
import { EmptyState } from '@/components/common/EmptyState';
import { ActionMenu, type ActionMenuItem } from '@/components/common/ActionMenu';
import { HelpTip } from './HelpTip';
import type {
  EntityTableConfig,
  EntityListResult,
  EntityActionKey,
} from './types';

const ROW_ACTION_LABELS: Record<EntityActionKey, string> = {
  view: 'View',
  edit: 'Edit',
  archive: 'Archive',
  activate: 'Activate',
};

const ACTION_ICONS: Record<EntityActionKey, React.ReactNode> = {
  view: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  edit: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
    </svg>
  ),
  archive: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="3" width="20" height="5" rx="1" />
      <path d="M4 8v11a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V8M10 13h4" />
    </svg>
  ),
  activate: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  ),
};

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_WINDOW = 2; // pages shown either side of current page

// ── Styled components ────────────────────────────────────────────────────────

const Root = styled.div``;

const FiltersRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: ${({ theme }) => theme.spacing.sm};
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
`;

const SearchWrapper = styled.div`
  width: 280px;
  @media (max-width: 768px) {
    width: 100%;
    order: -1;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
  flex: 1;
`;

const CountBadge = styled.span`
  margin-left: auto;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: nowrap;
  align-self: center;
`;

// Desktop table

const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  @media (max-width: 768px) {
    display: none;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 560px;
`;

const Thead = styled.thead`
  background: ${({ theme }) => theme.colors.backgroundTertiary};
`;

const Th = styled.th<{ $sortable?: boolean; $width?: string }>`
  padding: 12px ${({ theme }) => theme.spacing.lg};
  text-align: left;
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  color: ${({ theme }) => theme.colors.textSecondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  white-space: nowrap;
  width: ${({ $width }) => $width ?? 'auto'};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  user-select: ${({ $sortable }) => ($sortable ? 'none' : 'auto')};
  cursor: ${({ $sortable }) => ($sortable ? 'pointer' : 'default')};
  &:hover {
    color: ${({ $sortable, theme }) => ($sortable ? theme.colors.text : theme.colors.textSecondary)};
  }
`;

const ThContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr<{ $clickable?: boolean; $selected?: boolean }>`
  transition: background ${({ theme }) => theme.transitions.fast};
  position: relative;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  background: ${({ $selected, theme }) => ($selected ? theme.colors.primaryLight : 'transparent')};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
  &:hover .row-actions {
    opacity: 1;
  }
`;

const Td = styled.td`
  padding: 12px ${({ theme }) => theme.spacing.lg};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  vertical-align: middle;

  ${Tr}:last-child & {
    border-bottom: none;
  }
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: ${({ theme }) => theme.colors.primary};
`;

const SkeletonCell = styled.div`
  height: 16px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  animation: pulse 1.5s ease-in-out infinite;
  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.4;
    }
  }
`;

// Row action menu

const ActionMenuWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const ActionsContainer = styled.div`
  opacity: 0;
  transition: opacity 0.15s ease;
  display: flex;
  gap: 4px;
`;

// Mobile card grid

const MobileCardGrid = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.md};
  }
`;

const MobileCard = styled.div<{ $clickable?: boolean; $selected?: boolean }>`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid
    ${({ $selected, theme }) => ($selected ? theme.colors.primary : theme.colors.border)};
  border-left: ${({ $selected, theme }) =>
    $selected ? `4px solid ${theme.colors.primary}` : `1px solid ${theme.colors.border}`};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.md};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const MobileCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const MobileCardTitle = styled.div`
  flex: 1;
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
`;

const MobileCardBody = styled.div`
  display: grid;
  gap: 6px;
`;

const MobileCardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  padding: 4px 0;
  border-bottom: 1px dashed ${({ theme }) => theme.colors.borderLight};
  &:last-child {
    border-bottom: none;
  }
`;

const MobileCardLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  min-width: 80px;
  flex-shrink: 0;
`;

const MobileCardValue = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  text-align: right;
  flex: 1;
`;

// Pagination

const PaginationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding-top: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
`;

const Ellipsis = styled.span`
  padding: 0 4px;
  color: ${({ theme }) => theme.colors.textTertiary};
`;

const PageBtn = styled.button<{ $active?: boolean }>`
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border: 1px solid
    ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ $active, theme }) => ($active ? theme.colors.primary : 'transparent')};
  color: ${({ $active, theme }) => ($active ? theme.colors.textInverse : theme.colors.text)};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  &:hover:not(:disabled):not([data-active='true']) {
    background: ${({ theme }) => theme.colors.backgroundTertiary};
  }
`;

const MobilePaginationRow = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding-top: ${({ theme }) => theme.spacing.md};
  }
`;

const MobilePageInfo = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const DesktopPaginationRow = styled(PaginationRow)`
  @media (max-width: 768px) {
    display: none;
  }
`;

const ErrorBox = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
  text-align: center;
  color: ${({ theme }) => theme.colors.error};
`;

// ── Component ────────────────────────────────────────────────────────────────

interface EntityTableProps<T extends { id: string }> {
  config: EntityTableConfig<T>;
  result: EntityListResult<T> | undefined;
  isLoading?: boolean;
  error?: Error | null;
  sort: string;
  order: 'asc' | 'desc';
  search: string;
  filters: Record<string, string>;
  page: number;
  selectedIds: string[];
  onSortChange: (sort: string, order: 'asc' | 'desc') => void;
  onSearchChange: (search: string) => void;
  onFilterChange: (key: string, value: string) => void;
  onPageChange: (page: number) => void;
  onSelectChange: (ids: string[]) => void;
  onRowClick: (item: T) => void;
  onRowAction: (action: EntityActionKey, item: T) => void;
  onRetry?: () => void;
  /**
   * 11E.5 (D9) — true when the owner tenant switcher is in "All" mode.
   * Read-only: adds a synthetic Tenant column (from `item._tenantId`),
   * restricts row actions to 'view' only, and hides pagination (fan-out
   * results are merged, not paginated across tenants).
   */
  isAllMode?: boolean;
}

export function EntityTable<T extends { id: string }>({
  config,
  result,
  isLoading,
  error,
  sort,
  order,
  search,
  filters,
  page,
  selectedIds,
  onSortChange,
  onSearchChange,
  onFilterChange,
  onPageChange,
  onSelectChange,
  onRowClick,
  onRowAction,
  onRetry,
  isAllMode,
}: EntityTableProps<T>) {
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [searchInput, setSearchInput] = useState(search);
  const [openActionRowId, setOpenActionRowId] = useState<string | null>(null);

  const handleSearchInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchInput(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => onSearchChange(value), SEARCH_DEBOUNCE_MS);
    },
    [onSearchChange],
  );

  const handleHeaderClick = useCallback(
    (colKey: string) => {
      if (sort === colKey) {
        onSortChange(colKey, order === 'asc' ? 'desc' : 'asc');
      } else {
        onSortChange(colKey, 'asc');
      }
    },
    [sort, order, onSortChange],
  );

  const handleSelectAll = useCallback(() => {
    if (!result) return;
    if (selectedIds.length === result.items.length) {
      onSelectChange([]);
    } else {
      onSelectChange(result.items.map((i) => i.id));
    }
  }, [result, selectedIds, onSelectChange]);

  const handleSelectOne = useCallback(
    (id: string, checked: boolean) => {
      if (checked) {
        onSelectChange([...selectedIds, id]);
      } else {
        onSelectChange(selectedIds.filter((i) => i !== id));
      }
    },
    [selectedIds, onSelectChange],
  );

  const items = result?.items ?? [];
  const total = result?.total ?? 0;
  const pages = result?.pages ?? 1;

  const primaryCol = config.columns.find((c) => c.isPrimary) ?? config.columns[0];
  const mobileBodyCols = config.columns.filter(
    (c) => !c.hideOnMobile && c.key !== primaryCol?.key && c.key !== 'actions',
  );

  // ── Pagination pages array ──────────────────────────────────────────────
  const pageNumbers: Array<number | '...'> = [];
  if (pages <= 7) {
    for (let i = 1; i <= pages; i++) pageNumbers.push(i);
  } else {
    pageNumbers.push(1);
    const lo = Math.max(2, page - PAGE_WINDOW);
    const hi = Math.min(pages - 1, page + PAGE_WINDOW);
    if (lo > 2) pageNumbers.push('...');
    for (let i = lo; i <= hi; i++) pageNumbers.push(i);
    if (hi < pages - 1) pageNumbers.push('...');
    pageNumbers.push(pages);
  }

  // ── Row actions renderer ────────────────────────────────────────────────
  const renderRowActions = (item: T, alwaysVisible = false) => {
    // 11E.5 (D9): "All" mode is read-only — only 'view' survives regardless
    // of config, and mutating buttons never render.
    const actions = isAllMode ? config.rowActions.filter((a) => a === 'view') : config.rowActions;
    if (!actions.length) return null;

    const menuItems: ActionMenuItem[] = actions.map((action) => ({
      key: action,
      label: ROW_ACTION_LABELS[action],
      icon: ACTION_ICONS[action],
      destructive: action === 'archive',
      confirm:
        action === 'archive'
          ? {
              title: `Archive this ${config.entityName}?`,
              description: 'It will be hidden from active lists. You can reactivate it later.',
              confirmLabel: 'Archive',
            }
          : undefined,
      onSelect: () => onRowAction(action, item),
    }));

    // Mobile cards have no hover state (touch), so their trigger is always
    // visible rather than gated behind the desktop row-hover reveal.
    const visible = alwaysVisible || openActionRowId === item.id;

    return (
      <ActionsContainer className="row-actions" style={visible ? { opacity: 1 } : undefined}>
        <ActionMenu
          items={menuItems}
          triggerLabel={`Actions for this ${config.entityName}`}
          // Row id included: without it every row's trigger shares one
          // testid, so getByTestId matches N elements (Playwright strict-mode
          // violation) and specs can only reach a row by scoping through its
          // row locator first. The menu itself is portalled to <body>, so its
          // item testids are global — they need the row id even more.
          testId={`${config.testIdPrefix}-actionmenu-row-${item.id}`}
          onOpenChange={(open) => setOpenActionRowId(open ? item.id : null)}
        />
      </ActionsContainer>
    );
  };

  // ── Body content ────────────────────────────────────────────────────────
  const renderDesktopBody = () => {
    if (isLoading) {
      return Array.from({ length: 5 }).map((_, ri) => (
        <Tr key={ri}>
          <Td><Checkbox type="checkbox" disabled /></Td>
          {isAllMode && <Td />}
          {config.columns.map((col) => (
            <Td key={col.key}>
              <SkeletonCell style={{ width: col.width ?? '80%' }} />
            </Td>
          ))}
          <Td />
        </Tr>
      ));
    }

    if (!items.length) {
      return (
        <tr>
          <td colSpan={config.columns.length + (isAllMode ? 3 : 2)}>
            <EmptyState
              title={`No ${config.entityName}s found`}
              description="Try adjusting your search or filters."
            />
          </td>
        </tr>
      );
    }

    return items.map((item) => (
      <Tr
        key={item.id}
        $clickable
        $selected={selectedIds.includes(item.id)}
        onClick={() => onRowClick(item)}
        data-testid={`${config.testIdPrefix}-row-${item.id}`}
        data-flow={`${config.entityName}-row`}
      >
        <Td onClick={(e) => e.stopPropagation()}>
          <Checkbox
            type="checkbox"
            checked={selectedIds.includes(item.id)}
            onChange={(e) => handleSelectOne(item.id, e.target.checked)}
            data-testid={`${config.testIdPrefix}-row-checkbox-${item.id}`}
          />
        </Td>
        {isAllMode && (
          <Td data-testid={`${config.testIdPrefix}-row-tenant-${item.id}`}>
            {String((item as Record<string, unknown>)._tenantId ?? '')}
          </Td>
        )}
        {config.columns.map((col) => (
          <Td key={col.key}>
            {col.render
              ? col.render(item)
              : String((item as Record<string, unknown>)[col.key] ?? '')}
          </Td>
        ))}
        <Td onClick={(e) => e.stopPropagation()}>
          <ActionMenuWrapper>{renderRowActions(item)}</ActionMenuWrapper>
        </Td>
      </Tr>
    ));
  };

  // ── Pagination ──────────────────────────────────────────────────────────
  const renderPagination = () => {
    // 11E.5 (D9): "All" mode merges per-tenant fan-out results — there is no
    // single combined page count, so pagination controls are hidden entirely
    // rather than showing a misleading page count.
    if (isAllMode || pages <= 1) return null;

    return (
      <>
        {/* Desktop: numbered pages */}
        <DesktopPaginationRow data-testid={`${config.testIdPrefix}-pagination`}>
          <PageBtn
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            aria-label="Previous page"
          >
            ‹
          </PageBtn>
          {pageNumbers.map((p, idx) =>
            p === '...' ? (
              <Ellipsis key={`ellipsis-${idx}`}>
                …
              </Ellipsis>
            ) : (
              <PageBtn
                key={p}
                $active={p === page}
                data-active={p === page ? 'true' : undefined}
                onClick={() => onPageChange(p as number)}
                aria-label={`Page ${p}`}
                aria-current={p === page ? 'page' : undefined}
              >
                {p}
              </PageBtn>
            ),
          )}
          <PageBtn
            disabled={page >= pages}
            onClick={() => onPageChange(page + 1)}
            aria-label="Next page"
          >
            ›
          </PageBtn>
        </DesktopPaginationRow>

        {/* Mobile: prev/next only */}
        <MobilePaginationRow>
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            ← Prev
          </Button>
          <MobilePageInfo>
            {page} / {pages}
          </MobilePageInfo>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= pages}
            onClick={() => onPageChange(page + 1)}
          >
            Next →
          </Button>
        </MobilePaginationRow>
      </>
    );
  };

  // ── Main render ─────────────────────────────────────────────────────────

  if (error) {
    return (
      <ErrorBox data-testid={`${config.testIdPrefix}-error`}>
        <p>{error.message ?? `Failed to load ${config.entityName}s.`}</p>
        {onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry}>
            Retry
          </Button>
        )}
      </ErrorBox>
    );
  }

  return (
    <Root>
      {/* ── Filters + search row ────────────────────────────────────── */}
      <FiltersRow>
        <SearchWrapper>
          <SearchBar
            placeholder={`Search ${config.entityName}s…`}
            value={searchInput}
            onChange={handleSearchInputChange}
            data-testid={`${config.testIdPrefix}-search`}
            data-flow={`${config.entityName}-search`}
          />
          <HelpTip tipKey={`${config.entityName}s.search`} />
        </SearchWrapper>
        <FilterGroup>
          {config.filters.map((filter) => (
            <div key={filter.key} style={{ display: 'flex', alignItems: 'center' }}>
              <Dropdown
                testId={`${config.testIdPrefix}-filter-${filter.key}`}
                options={filter.options}
                value={filters[filter.key] ?? filter.options[0]?.value ?? ''}
                onChange={(val) => onFilterChange(filter.key, val)}
              />
              {filter.helpKey && <HelpTip tipKey={filter.helpKey} />}
            </div>
          ))}
        </FilterGroup>
        <CountBadge data-testid={`${config.testIdPrefix}-count`}>
          {isLoading ? '…' : `${total.toLocaleString()} total`}
        </CountBadge>
      </FiltersRow>

      {/* ── Desktop table ───────────────────────────────────────────── */}
      <TableWrapper data-testid={`${config.testIdPrefix}-table-scroll`}>
        <StyledTable data-testid={`${config.testIdPrefix}-table`}>
          <Thead>
            <tr>
              <Th $width="40px">
                <Checkbox
                  type="checkbox"
                  checked={items.length > 0 && selectedIds.length === items.length}
                  onChange={handleSelectAll}
                  aria-label="Select all"
                />
              </Th>
              {isAllMode && (
                <Th $width="120px" data-testid={`${config.testIdPrefix}-column-tenant`}>
                  Tenant
                </Th>
              )}
              {config.columns.map((col) => (
                <Th
                  key={col.key}
                  $sortable={col.sortable}
                  $width={col.width}
                  onClick={() => col.sortable && handleHeaderClick(col.key)}
                  data-testid={
                    col.sortable ? `${config.testIdPrefix}-sort-${col.key}` : undefined
                  }
                >
                  <ThContent>
                    {col.header}
                    {col.sortable && sort === col.key && (
                      <span style={{ marginLeft: 4 }}>{order === 'asc' ? '↑' : '↓'}</span>
                    )}
                    {col.helpKey && <HelpTip tipKey={col.helpKey} />}
                  </ThContent>
                </Th>
              ))}
              <Th $width="120px" /> {/* actions column */}
            </tr>
          </Thead>
          <Tbody>{renderDesktopBody()}</Tbody>
        </StyledTable>
      </TableWrapper>

      {/* ── Mobile card grid ────────────────────────────────────────── */}
      <MobileCardGrid>
        {isLoading ? (
          <EmptyState title="Loading…" description="" />
        ) : !items.length ? (
          <EmptyState
            title={`No ${config.entityName}s found`}
            description="Try adjusting your search or filters."
          />
        ) : (
          items.map((item) => (
            <MobileCard
              key={item.id}
              $clickable
              $selected={selectedIds.includes(item.id)}
              onClick={() => onRowClick(item)}
              data-testid={`${config.testIdPrefix}-row-${item.id}`}
            >
              <MobileCardHeader>
                <MobileCardTitle>
                  {primaryCol?.render
                    ? primaryCol.render(item)
                    : String((item as Record<string, unknown>)[primaryCol?.key ?? ''] ?? '')}
                </MobileCardTitle>
                <div onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    type="checkbox"
                    checked={selectedIds.includes(item.id)}
                    onChange={(e) => handleSelectOne(item.id, e.target.checked)}
                  />
                </div>
              </MobileCardHeader>
              <MobileCardBody>
                {isAllMode && (
                  <MobileCardRow data-testid={`${config.testIdPrefix}-row-tenant-${item.id}`}>
                    <MobileCardLabel>Tenant</MobileCardLabel>
                    <MobileCardValue>
                      {String((item as Record<string, unknown>)._tenantId ?? '')}
                    </MobileCardValue>
                  </MobileCardRow>
                )}
                {mobileBodyCols.map((col) => (
                  <MobileCardRow key={col.key}>
                    <MobileCardLabel>{col.header}</MobileCardLabel>
                    <MobileCardValue>
                      {col.render
                        ? col.render(item)
                        : String((item as Record<string, unknown>)[col.key] ?? '')}
                    </MobileCardValue>
                  </MobileCardRow>
                ))}
              </MobileCardBody>
              <div
                style={{ marginTop: 8, display: 'flex', gap: 6 }}
                onClick={(e) => e.stopPropagation()}
              >
                {renderRowActions(item, true)}
              </div>
            </MobileCard>
          ))
        )}
      </MobileCardGrid>

      {/* ── Pagination ──────────────────────────────────────────────── */}
      {renderPagination()}
    </Root>
  );
}



